// ============================================================================
// LAYOUT.JS - LAYOUT CON NOTIFICACIONES DE COMENTARIOS - ACTUALIZADO
// ============================================================================

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  RefreshCw,
  LogOut,
  User,
  Users,
  BookOpen,
  Settings,
  Search,
  Plus,
  X
} from 'lucide-react';
import { useTaskStore } from '../store/TaskStore';
import { shallow } from 'zustand/shallow';
import { useAuth } from '../auth/AuthContext';
import NotificationDropdown from '../components/NotificationDropdown';
import Avatar from '../components/Avatar';
import AddonTabs from '../components/AddonTabs';
import clientConfigService from '../services/clientConfigService';
import { useHasHover } from './shared/useMediaQuery';

// 🆕 IMPORTS PARA TOAST
import { ToastProvider } from '../contexts/ToastContext';
import ToastContainer from '../components/ToastContainer';
import useToast from '../services/useToast';
import { Shield } from 'lucide-react';

// Layout monta el ToastProvider, así que el consumo de useToast() tiene que
// vivir en un componente hijo (LayoutContent) para quedar dentro del árbol
// del Provider.
function Layout({ children }) {
  return (
    <ToastProvider>
      <LayoutContent>{children}</LayoutContent>
    </ToastProvider>
  );
}

function LayoutContent({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const userMenuRef = useRef(null);
  const userButtonRef = useRef(null);
  // Buscador con foco: en teléfono crece y oculta logo y Crear (layout.css).
  const [isSearching, setIsSearching] = useState(false);
  // Sin ratón (móvil, tableta) no hay ⌘K que anunciar: placeholder corto.
  const hasHover = useHasHover();
  const [clientConfig, setClientConfig] = useState({
    logo_url: null,
    // Default = crawl y gads visibles (mismo default que el backend
    // pre-migración): evita el parpadeo de la barra hasta que carga la config.
    // changelog es opt-in (backend default false): NO va en el fallback.
    addons: { crawl: true, gads: true },
    // Orden por defecto de las pestañas hasta que carga la config del cliente.
    addons_order: ['crawl', 'gads', 'changelog']
  });
  
  
  // Usar autenticación real
  const {
    user,
    logout: authLogout,
    isAdmin
  } = useAuth();

  // Mantener TaskStore para funcionalidades de tareas
  const {
    fetchTasks,
    fetchSprints,
    isLoading,
    searchTerm,
    setSearchTerm
  } = useTaskStore(
    (s) => ({
      fetchTasks: s.fetchTasks,
      fetchSprints: s.fetchSprints,
      isLoading: s.isLoading,
      searchTerm: s.searchTerm,
      setSearchTerm: s.setSearchTerm
    }),
    shallow
  );

  const { showError, showWarning } = useToast();

  // Atajo de la command palette (⌘K en Mac, Ctrl+K en el resto) anunciado en el
  // placeholder de búsqueda — así el cliente descubre que puede pulsarlo.
  const isMac = typeof navigator !== 'undefined' &&
    /Mac|iPhone|iPad|iPod/.test(navigator.platform || navigator.userAgent || '');
  const searchPlaceholder = !hasHover
    ? 'Buscar'
    : (isMac ? 'Busca o pulsa ⌘K' : 'Busca o pulsa Ctrl+K');

  // Búsqueda global: filtra la tabla de tareas; si no estamos en el listado, navegar a él
  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    // El buscador filtra in-situ en el dashboard y en el histórico de sprints
    // (ambas son vistas de tareas). Desde cualquier otra pantalla, lleva al
    // dashboard para que se vean los resultados.
    const p = location.pathname.replace(/\/+$/, '');
    // El módulo Changelog también consume el término global (filtro q), así que
    // NO navegamos fuera al teclear estando en él (igual que dashboard/sprints).
    // Crawl/Gads no lo consumen y mantienen su comportamiento (llevan al listado).
    // Creatividades: su listado también consume el término global; desde una
    // ficha o el formulario se navega AL LISTADO de creatividades (no al sprint).
    if (p.startsWith('/task/creatives')) {
      if (p !== '/task/creatives') navigate('/task/creatives');
      return;
    }
    if (p !== '/task' && p !== '/task/sprint-history' && p !== '/task/changelog') {
      navigate('/task');
    }
  };

  useEffect(() => {
    const loadConfig = async () => {
      const config = await clientConfigService.fetchConfig();
      setClientConfig(config);
    };

    loadConfig();

    // Suscribirse a cambios
    const unsubscribe = clientConfigService.subscribe((config) => {
      setClientConfig(config);
    });

    return unsubscribe;
  }, []);

  // Logout usa la función real de autenticación
  const handleLogout = async () => {
    console.log('🚪 Logout clicked');
    setShowUserDropdown(false);
    try {
      await authLogout();
      // El AuthContext automáticamente redirigirá al login
    } catch (error) {
      console.error('Error during logout:', error);
      // Forzar logout local si hay error
      window.location.reload();
    }
  };

  // Función de recarga de datos desde el header
  const handleReloadData = async () => {
    try {
      console.log('🔄 Reloading data from header...');
      await Promise.all([
        fetchTasks(),
        fetchSprints()
      ]);
      console.log('✅ Data reloaded successfully');
    } catch (error) {
      console.error('⚠️ Error reloading data:', error);
      showError('Error al recargar los datos');
    }
  };

  // Ver documentos
  const handleViewDocuments = () => {
    console.log('📚 Navigate to documentation');
    navigate('/task/documentation');
  };

  // Panel de gestión de usuarios (solo admins)
  const handleUserManagement = () => {
    if (!isAdmin()) {
      showWarning('Solo los administradores pueden gestionar usuarios');
      return;
    }
    console.log('👥 Navigate to user management');
    setShowUserDropdown(false);
    navigate('/task/users');
  };

  // Effect para manejar clics fuera del dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target)) {
        setShowUserDropdown(false);
      }
    };

    const handleEscKey = (event) => {
      if (event.key === 'Escape') {
        setShowUserDropdown(false);
        // Devuelve el foco al avatar que abrió el menú.
        userButtonRef.current?.focus();
      }
    };

    if (showUserDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [showUserDropdown]);

  return (
      <div className="layout">
        {/* Header minimalista */}
        <header className="header">
          <div className={`header-content${isSearching ? ' is-searching' : ''}`}>
            {/* Left - Logo propio + logo cliente */}
            <div className="header-left">
              <button
                className="header-brand"
                onClick={() => navigate('/task')}
                title="Inicio"
              >
                <img
                  src={`${process.env.PUBLIC_URL}/brand-logo.png`}
                  alt="Jonathanva"
                  className="header-logo"
                />
              </button>
              {clientConfig.logo_url && (
                <img
                  src={clientConfig.logo_url}
                  alt={clientConfig.nombre_empresa || 'Cliente'}
                  className="header-client-logo"
                />
              )}
            </div>

            {/* Center - Búsqueda global + Crear */}
            <div className="header-center">
              <div className={`header-search${searchTerm ? ' has-term' : ''}`}>
                <Search size={14} className="header-search-icon" />
                <input
                  type="text"
                  className="header-search-input"
                  placeholder={searchPlaceholder}
                  aria-label="Buscar tareas"
                  onFocus={() => setIsSearching(true)}
                  onBlur={() => setIsSearching(false)}
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
                {searchTerm && (
                  <button
                    className="header-search-clear"
                    // No roba el foco al input: si lo hiciera, la cabecera se
                    // recoloca entre mousedown y mouseup y el toque se pierde.
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => setSearchTerm('')}
                    title="Limpiar búsqueda"
                  >
                    <X size={13} />
                  </button>
                )}
              </div>
              <button
                className="header-create-btn"
                onClick={() => navigate('/task/create')}
              >
                <Plus size={15} />
                <span>Crear</span>
              </button>
            </div>

            {/* Right - Actions & User */}
            <div className="header-right">
              {/* Notificaciones de comentarios */}
              <NotificationDropdown />

              {/* Documentación - acceso global desde cualquier pantalla */}
              <button
                onClick={handleViewDocuments}
                className="header-action-button u-hide-phone"
                title="Documentación"
                aria-label="Documentación"
              >
                <BookOpen size={16} />
              </button>

              {/* Refresh button */}
              <button
                onClick={handleReloadData}
                disabled={isLoading}
                className="header-action-button u-hide-phone"
                title="Recargar datos"
                aria-label="Recargar datos"
              >
                <RefreshCw size={16} className={isLoading ? 'animate-spin' : ''} />
              </button>

              {/* User menu - solo avatar, estilo chip */}
              <div className="user-menu" ref={userMenuRef}>
                <button
                  ref={userButtonRef}
                  onClick={() => setShowUserDropdown(!showUserDropdown)}
                  className="user-button"
                  title={user?.nombre || 'Usuario'}
                  aria-label={user?.nombre ? `Menú de ${user.nombre}` : 'Menú de usuario'}
                  aria-haspopup="menu"
                  aria-expanded={showUserDropdown}
                >
                  <Avatar
                    name={user?.nombre}
                    src={user?.avatar}
                    size={28}
                    bg="#d3caff"
                    className="user-avatar"
                  />
                </button>

                {showUserDropdown && (
                  <div className="user-dropdown">
                    {/* Header del dropdown */}
                    <div className="user-dropdown-header">
                      <Avatar
                        name={user?.nombre}
                        src={user?.avatar}
                        size={36}
                        bg="#d3caff"
                        className="user-dropdown-avatar"
                      />
                      <div className="user-dropdown-info">
                        <div className="user-dropdown-name">
                          {user?.nombre || 'Usuario'}
                          {isAdmin() && (
                            <span className="user-role-badge admin-badge dropdown-badge">
                              <Shield size={10} />
                              Admin
                            </span>
                          )}
                        </div>
                        <div className="user-dropdown-email">{user?.email || user?.rol || ''}</div>
                      </div>
                    </div>

                    <div className="user-dropdown-divider"></div>

                    {/* Menú del dropdown */}
                    <div className="user-dropdown-menu">
                      {/* Teléfono: documentación y recargar salen de la cabecera
                          (no caben junto al buscador) y se agrupan aquí. */}
                      <button
                        className="user-dropdown-item u-only-phone"
                        onClick={() => {
                          setShowUserDropdown(false);
                          handleViewDocuments();
                        }}
                      >
                        <BookOpen size={16} />
                        <span>Documentación</span>
                      </button>
                      <button
                        className="user-dropdown-item u-only-phone"
                        disabled={isLoading}
                        onClick={() => {
                          setShowUserDropdown(false);
                          handleReloadData();
                        }}
                      >
                        <RefreshCw size={16} />
                        <span>Recargar datos</span>
                      </button>
                      <div className="user-dropdown-divider u-only-phone"></div>
                      {/* Gestionar usuarios - Solo visible para administradores */}
                      {isAdmin() && (
                        <>
                          <button
                            className="user-dropdown-item admin-item"
                            onClick={handleUserManagement}
                          >
                            <Users size={16} />
                            <span>Gestionar usuarios</span>
                          </button>
                          
                        </>
                      )}
                      {/* Configuración Cliente - Solo visible para administradores */}
                      {isAdmin() && (
                        <button 
                          className="user-dropdown-item admin-item"
                          onClick={() => {
                            setShowUserDropdown(false);
                            navigate('/task/client-config');
                          }}
                        >
                          <Settings size={16} />
                          <span>Configuración Cliente</span>
                        </button>
                      )}

                      {isAdmin() && <div className="user-dropdown-divider"></div>}
                       <button
                          className="user-dropdown-item"
                          onClick={() => {
                            setShowUserDropdown(false);
                            navigate('/task/profile');
                          }}
                        >
                          <User size={16} />
                          <span>Mi Perfil</span>
                        </button>

                        <div className="user-dropdown-divider"></div>

                      {/* Cerrar sesión - Visible para todos */}
                      <button 
                        className="user-dropdown-item logout-item"
                        onClick={handleLogout}
                      >
                        <LogOut size={16} />
                        <span>Cerrar sesión</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Barra de módulos/addons: base "Tareas" + pestañas de los addons
            activos del cliente. Entre la cabecera y el contenido. */}
        <AddonTabs addons={clientConfig.addons} order={clientConfig.addons_order} />

        {/* Contenido principal */}
        <main className="main-content">
          {children}
        </main>

        {/* 🆕 TOAST CONTAINER */}
        <ToastContainer position="top-right" />
      </div>
  );
}

export default Layout;